import React, { useEffect, useState, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import bowser from 'yola-bowser';
import Box from '../../box';
import Icon from '../../icon';
import Slider from '../../slider';
import Paragraph from '../../paragraph';
import ValueInput from '../../value-input';
import IconButton from '../../icon-button';
import HorizontalStack from '../../horizontal-stack';
import { Popover, PopoverContent, PopoverTrigger } from '../../popover';
import { iconsList } from '../../icon/glyphs';
import Heading from '../../heading';
import ValuePopover from './value-popover';

const BasicSliderPattern = ({
  iconGlyph,
  min,
  max,
  startPoint,
  value,
  selected = false,
  indeterminate = false,
  isValueEditable = true,
  mixedLabel,
  title,
  step,
  popoverProps = null,
  valuePopoverProps = null,
  onChange = Function.prototype,
  onAfterChange = Function.prototype,
  onClick = null,
}) => {
  const elementRef = useRef(null);
  const [isValuePopoverVisible, setIsValuePopoverVisible] = useState(false);

  const isTouchDevice = bowser.mobile || bowser.tablet;
  const popoverMode = isTouchDevice ? 'popover' : 'tooltip';
  const inputValue = indeterminate ? mixedLabel : value;
  const isValuePopoverEnabled = !!valuePopoverProps;
  const hasValuePopoverChecker =
    isValuePopoverEnabled && typeof valuePopoverProps?.checkVisibility === 'function';

  const showValuePopover = useCallback(() => setIsValuePopoverVisible(true), []);
  const hideValuePopover = useCallback(() => setIsValuePopoverVisible(false), []);
  const toggleValuePopover = useCallback(
    (newValue) => {
      if (hasValuePopoverChecker) {
        const valuePopovervisibility = valuePopoverProps.checkVisibility(Number(newValue));

        if (valuePopovervisibility) {
          showValuePopover();
        } else {
          hideValuePopover();
        }
      }
    },
    [hasValuePopoverChecker, valuePopoverProps, showValuePopover, hideValuePopover]
  );

  const handleChange = useCallback(
    (newValue) => {
      onChange(newValue);
      toggleValuePopover(newValue);
    },
    [onChange, toggleValuePopover]
  );

  const handleValueInputBlur = useCallback(
    (newValue) => {
      onAfterChange(newValue);

      if (isValuePopoverEnabled) {
        hideValuePopover();
      }
    },
    [isValuePopoverEnabled, hideValuePopover, onAfterChange]
  );

  useEffect(() => {
    if (hasValuePopoverChecker && isValuePopoverVisible) {
      document.addEventListener('scroll', hideValuePopover, true);

      return () => document.removeEventListener('scroll', hideValuePopover, true);
    }

    return () => {};
  }, [hasValuePopoverChecker, isValuePopoverVisible, hideValuePopover]);

  useEffect(() => {
    if (hasValuePopoverChecker && isValuePopoverVisible) {
      const handleOutsideClick = (event) => {
        if (elementRef.current && !elementRef.current.contains(event.target)) {
          hideValuePopover();
        }
      };

      document.addEventListener('mousedown', handleOutsideClick);
      document.addEventListener('touchstart', handleOutsideClick);

      return () => {
        document.removeEventListener('mousedown', handleOutsideClick);
        document.removeEventListener('touchstart', handleOutsideClick);
      };
    }

    return () => {};
  }, [hasValuePopoverChecker, isValuePopoverVisible, hideValuePopover]);

  const renderIconType = () => {
    if (!!popoverProps && !selected) {
      return (
        <Popover
          mode={popoverMode}
          placement="top-left"
          title={popoverProps.title}
          description={popoverProps.content}
          appearance="dark"
          imgSrc={popoverProps.imgSrc}
          {...(isValuePopoverEnabled && {
            onHover: hideValuePopover,
          })}
        >
          <PopoverTrigger>
            <IconButton
              appearance="light-accent"
              glyph={iconGlyph}
              selected={selected}
              onClick={onClick}
              size="large"
            />
          </PopoverTrigger>
          <PopoverContent />
        </Popover>
      );
    }

    if (onClick) {
      return (
        <IconButton
          appearance="light-accent"
          glyph={iconGlyph}
          selected={selected}
          onClick={onClick}
          size="large"
        />
      );
    }

    return (
      <div className="ws-ui-slider-pattern__icon-wrapper">
        <Icon size={20} glyph={iconGlyph} />
      </div>
    );
  };

  return (
    <div
      ref={elementRef}
      className="ws-ui-slider-pattern__box"
      {...(hasValuePopoverChecker && {
        onMouseLeave: hideValuePopover,
      })}
    >
      <HorizontalStack gap="spacing-xs" verticalAlign="center">
        <div className="ws-ui-slider-pattern__content">
          {renderIconType()}
          {selected ? (
            <Box>
              <Paragraph>{title}</Paragraph>
            </Box>
          ) : (
            <Slider
              min={min}
              max={max}
              startPoint={startPoint}
              value={value}
              size="small"
              indeterminate={indeterminate}
              step={step}
              onChange={handleChange}
              onAfterChange={onAfterChange}
              {...(isValuePopoverEnabled && {
                onFocus: () => toggleValuePopover(value),
                onBlur: hideValuePopover,
              })}
            />
          )}
        </div>
        {selected ? (
          <Heading type="heading-6">{mixedLabel}</Heading>
        ) : (
          <div
            className="ws-ui-slider-pattern__value-wrapper"
            {...(!hasValuePopoverChecker && {
              onMouseEnter: showValuePopover,
              onMouseLeave: hideValuePopover,
            })}
          >
            {isValuePopoverEnabled && (
              <ValuePopover
                mode={popoverMode}
                value={inputValue}
                isOpen={isValuePopoverVisible}
                {...valuePopoverProps}
              />
            )}

            {isValueEditable ? (
              <ValueInput
                value={inputValue}
                min={min}
                max={max}
                onChange={onChange}
                onBlur={handleValueInputBlur}
                {...(isValuePopoverEnabled && {
                  onInputChange: toggleValuePopover,
                })}
              />
            ) : (
              <Heading type="heading-6" singleLine>
                {inputValue}
              </Heading>
            )}
          </div>
        )}
      </HorizontalStack>
    </div>
  );
};

BasicSliderPattern.propTypes = {
  title: PropTypes.string.isRequired,
  mixedLabel: PropTypes.string.isRequired,
  iconGlyph: PropTypes.oneOf(iconsList).isRequired,
  value: PropTypes.number.isRequired,
  startPoint: PropTypes.number.isRequired,
  min: PropTypes.number.isRequired,
  max: PropTypes.number.isRequired,
  step: PropTypes.number.isRequired,
  popoverProps: PropTypes.shape({
    title: PropTypes.node,
    content: PropTypes.node,
    imgSrc: PropTypes.string,
  }),
  valuePopoverProps: PropTypes.shape({
    title: PropTypes.node,
    content: PropTypes.node,
    imgSrc: PropTypes.string,
    checkVisibility: PropTypes.func,
  }),
  selected: PropTypes.bool,
  indeterminate: PropTypes.bool,
  isValueEditable: PropTypes.bool,
  onChange: PropTypes.func,
  onAfterChange: PropTypes.func,
  onClick: PropTypes.func,
};

export default BasicSliderPattern;
